/**
 * As frases da tela "Como funciona", montadas do mapa e dos números (spec 2026-10-26 §3).
 * Nenhum prazo é escrito aqui: todos vêm de `regras_sla`, que o backend lê das constantes.
 */
import type { AtorDoCaminho, CaminhoDoFluxo, MapaDoFluxo, NumerosDoFluxo } from '@/api/chamados'
import { rotuloDoStatus } from '@/components/chamados/StatusBadge'
import { ESTADO_ANTERIOR, QUALQUER_ESTADO, origens, type Visao } from './visaoDoFluxo'

export const ROTULO_DO_ATOR: Record<AtorDoCaminho, string> = {
  pessoa: 'Uma pessoa',
  sistema: 'O sistema, sozinho',
  admin: 'Só o administrador',
}

export function rotuloDoEstado(estado: string, visao: Visao): string {
  if (estado === QUALQUER_ESTADO) return 'Qualquer estado aberto'
  if (estado === ESTADO_ANTERIOR) return 'O estado de antes do fechamento'
  // `requested` e `in_triage` dizem os dois "Aguardando triagem"; no mapa, `requested` é o
  // chamado devolvido à fila por encaminhamento, e o rótulo do StatusBadge para isso diz qual é.
  return rotuloDoStatus(estado, { visao, encaminhado: estado === 'requested' })
}

/** O que o relógio do SLA faz enquanto o chamado está neste estado. */
export function relogioNoEstado(estado: string, mapa: MapaDoFluxo): string {
  if (mapa.encerrados.includes(estado)) return 'O atendimento está encerrado: o prazo não corre.'
  if (mapa.pausam.includes(estado)) {
    return 'O prazo pausa aqui, quando a categoria do chamado é das que pausam.'
  }
  if (mapa.regras_sla.primeira_resposta_conta_em.includes(estado)) {
    return 'Corre o prazo da primeira resposta e o de solução.'
  }
  return 'O prazo de solução corre.'
}

/** O que o caminho muda no prazo, quando muda. */
export function efeitoNoPrazo(c: CaminhoDoFluxo, mapa: MapaDoFluxo): string | null {
  if (c.para === 'reopened' && mapa.regras_sla.reabrir_recomeca_o_prazo) {
    return mapa.regras_sla.reabrir_vencido_vira_critico
      ? 'Reabrir recomeça o prazo e, se o ciclo anterior tinha vencido, a prioridade vira crítica.'
      : 'Reabrir recomeça o prazo.'
  }
  if (mapa.pausam.includes(c.para)) return 'O prazo pausa, se a categoria pausa.'
  if (mapa.pausam.some((p) => origens(c).includes(p)) && !mapa.encerrados.includes(c.para)) {
    return 'O prazo volta a correr.'
  }
  return null
}

/** Uso do caminho em 90 dias. Contado por par de estados: `null` quando a tela não mostra uso. */
export function usoDoCaminho(c: CaminhoDoFluxo, numeros: NumerosDoFluxo | undefined): number | null {
  if (!numeros?.uso_90d || c.de === QUALQUER_ESTADO) return null
  return origens(c).reduce((total, de) => total + (numeros.uso_90d ?? [])
    .filter((u) => u.de === de && u.para === c.para)
    .reduce((s, u) => s + u.n, 0), 0)
}

/** O que cada permissão de chamados deixa fazer, nas palavras da descrição dela. */
const PODE: Record<string, string> = {
  request: 'abrir chamado',
  approve: 'aprovar chamado',
  assign: 'atribuir chamado',
  close: 'fechar chamado',
  status_transition: 'mudar o estado do chamado',
  gmud_manage: 'gerir GMUD',
  read: 'ver chamado',
}

/**
 * A condição do backend cita a permissão pelo código (`quem tem chamados:assign`), que
 * não diz nada a quem abre chamado. Aqui ela vira o que a permissão deixa fazer.
 */
export function condicaoLegivel(condicao: string): string {
  return condicao
    .replace(/quem tem /g, 'quem pode ')
    .replace(/chamados:(\w+)/g, (codigo, acao: string) => PODE[acao] ?? codigo)
}

/** Rótulos dos estados, sem repetir o mesmo texto, em minúsculas para entrar numa frase. */
export function listaDeEstados(estados: string[], visao: Visao): string[] {
  return [...new Set(estados.map((e) => rotuloDoEstado(e, visao).toLowerCase()))]
}

/** "3 chamados", "1 chamado": a contagem, nunca zero (zero não aparece). */
export const chamados = (n: number) => `${n} ${n === 1 ? 'chamado' : 'chamados'}`
